import { Route, Routes } from '@angular/router';
import { authGuard } from './core/auth/auth.guard';
import { adminGuard } from './core/auth/admin.guard';
import { ownerGuard } from './core/auth/owner.guard';
import { publicGuard } from './core/auth/public.guard';
import { configuracionResolver } from './core/services/configuracion.resolver';
import { environment } from '../environments/environment';

/**
 * Rutas de la app autenticada. Se declaran SIN `authGuard` ni `resolve`: los
 * agrega `protegida()` al exportar, de modo que ninguna ruta nueva pueda nacer
 * sin sesión ni sin la configuración global resuelta. Los guards extra
 * (`adminGuard`, `ownerGuard`) sí van declarados aquí y se conservan.
 */
const rutasProtegidas: Routes = [
    {
        path: 'dashboard',
        loadComponent: () => import('./features/dashboard/dashboard.component').then(m => m.DashboardComponent),
        data: { title: 'Dashboard General' }
    },
    {
        path: 'resumen-bancos',
        loadComponent: () => import('./features/dashboard/resumen-bancos-detalle/resumen-bancos-detalle.component').then(m => m.ResumenBancosDetalleComponent),
        data: { title: 'Resumen de Bancos' }
    },
    {
        path: 'ingresos',
        loadComponent: () => import('./features/ingresos/ingresos.component').then(m => m.IngresosComponent),
        data: { title: 'Ingesta de Ingresos' }
    },
    {
        path: 'ingresos/sin-vincular',
        loadComponent: () => import('./features/ingresos/sin-vincular/sin-vincular.component').then(m => m.SinVincularComponent),
        data: { title: 'Movimientos Sin Vincular' }
    },
    {
        path: 'ingresos/corte/:id',
        loadComponent: () => import('./features/ingresos/corte-detalle/corte-detalle.component').then(m => m.CorteDetalleComponent),
        data: { title: 'Detalle de Corte' }
    },
    {
        path: 'egresos',
        loadComponent: () => import('./features/egresos/egresos.component').then(m => m.EgresosComponent),
        data: { title: 'Ingesta de Egresos' }
    },
    {
        path: 'conciliacion',
        loadComponent: () => import('./features/conciliacion/conciliacion.component').then(m => m.ConciliacionComponent),
        data: { title: 'Conciliación Bancaria' }
    },
    {
        path: 'conciliacion/omitidos',
        loadComponent: () => import('./features/conciliacion/omitidos/omitidos.component').then(m => m.OmitidosComponent),
        data: { title: 'Movimientos sin fecha' }
    },
    {
        path: 'conciliacion/montos',
        loadComponent: () => import('./features/conciliacion/montos/montos.component').then(m => m.MontosComponent),
        data: { title: 'Montos por revisar' }
    },
    {
        path: 'estados-cuenta',
        loadComponent: () => import('./features/estados-cuenta/estados-cuenta.component').then(m => m.EstadosCuentaComponent),
        data: { title: 'Estados de Cuenta' }
    },
    {
        path: 'sass',
        loadComponent: () => import('./features/sass/sass.component').then(m => m.SassComponent),
        data: { title: 'Ventas SASS' }
    },
    {
        path: 'sass/:id',
        loadComponent: () => import('./features/sass/sass-detalle/sass-detalle.component').then(m => m.SassDetalleComponent),
        data: { title: 'Detalle de Ventas SASS' }
    },
    {
        path: 'pagos',
        loadComponent: () => import('./features/pagos/pagos.component').then(m => m.PagosComponent),
        data: { title: 'Programación de Pagos' }
    },
    {
        path: 'efectivo',
        loadComponent: () => import('./features/efectivo/efectivo.component').then(m => m.EfectivoComponent),
        data: { title: 'Efectivo y Dividendos' }
    },
    {
        path: 'informes',
        loadComponent: () => import('./features/informes/informes.component').then(m => m.InformesComponent),
        data: { title: 'Informes Financieros' }
    },
    {
        path: 'catalogos',
        loadComponent: () => import('./features/catalogos/catalogos.component').then(m => m.CatalogosComponent),
        data: { title: 'Catálogos y Configuración' }
    },
    {
        path: 'alertas',
        loadComponent: () => import('./features/alertas/alertas.component').then(m => m.AlertasComponent),
        data: { title: 'Motor de Alertas' }
    },
    {
        path: 'usuarios',
        loadComponent: () => import('./features/usuarios/usuarios.component').then(m => m.UsuariosComponent),
        canActivate: [adminGuard],
        data: { title: 'Control de Usuarios' }
    },
    {
        path: 'primer-login',
        loadComponent: () => import('./features/primer-login/primer-login.component').then(m => m.PrimerLoginComponent),
        data: { title: 'Configura tu cuenta' }
    },
    {
        path: 'perfil',
        loadComponent: () => import('./features/perfil/perfil.component').then(m => m.PerfilComponent),
        data: { title: 'Mi Cuenta' }
    },
    // Panel de desarrollo: solo se registra fuera de producción (fail-closed)
    // y solo para OWNER — las herramientas dev nunca son para el usuario final
    // ni para el admin del cliente. El backend además devuelve 404 en
    // /api/dev/* cuando APP_ENV=production y 403 sin rol OWNER.
    ...(environment.production ? [] : [{
        path: 'dev',
        loadComponent: () => import('./features/dev-panel/dev-panel.component').then(m => m.DevPanelComponent),
        canActivate: [ownerGuard],
        data: { title: 'Panel de Desarrollo' }
    }]),
];

/**
 * Envuelve una ruta para que exija sesión y espere la configuración global.
 *
 * `authGuard` primero (sin sesión no se resuelve nada) y la config global como
 * `resolve`: la navegación espera a tenerla, así que el periodo del
 * `PeriodoService` ya llega recortado al arranque cuando la vista construye.
 * @param ruta - Ruta declarada en `rutasProtegidas`; sus guards y resolvers propios se conservan después de los comunes.
 * @returns Una copia de la ruta con `authGuard` al frente de `canActivate` y `configuracion` en `resolve`.
 */
function protegida(ruta: Route): Route {
    return {
        ...ruta,
        canActivate: [authGuard, ...(ruta.canActivate ?? [])],
        resolve: { configuracion: configuracionResolver, ...(ruta.resolve ?? {}) },
    };
}

/**
 * Tabla de rutas de la aplicación.
 *
 * La raíz redirige al login; el login solo es accesible sin sesión (`publicGuard`) y todas las demás
 * pasan por `protegida()`, así que ninguna pantalla financiera se carga sin sesión ni sin configuración.
 */
export const routes: Routes = [
    { path: '', pathMatch: 'full', redirectTo: '/login' },
    {
        path: 'login',
        canActivate: [publicGuard],
        loadComponent: () => import('./features/login/login.component').then(m => m.LoginComponent)
    },
    ...rutasProtegidas.map(protegida),
];
